import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Banknote, RefreshCcw, Search, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';
import { ExternalTransfer, ExternalTransferStatus } from '../types/api';
import {
  getTransferUiStatus,
  normalizeExternalTransferStatus,
} from '../utils/externalTransferStatus';

type StatusVariant = 'pending' | 'completed' | 'unknown';

const statusVariantColors: Record<StatusVariant, string> = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  unknown: 'bg-slate-100 text-slate-700 border-slate-200',
};

const statusLabels: Record<ExternalTransferStatus, string> = {
  pending: 'Pendiente',
  completed: 'Aprobada',
};

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(amount);

const formatDateTime = (input?: string) => {
  if (!input) return '—';
  try {
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(input));
  } catch {
    return input;
  }
};

const getStatusVariant = (status?: string): StatusVariant => {
  const normalized = normalizeExternalTransferStatus(status);
  return normalized === 'pending' || normalized === 'completed' ? normalized : 'unknown';
};

const ExternalTransfersSection: React.FC = () => {
  const { getExternalTransfers, updateExternalTransferStatus, accessToken, isAuthenticated } =
    useAdminAPI();
  const [transfers, setTransfers] = useState<ExternalTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  const [updatingTransferId, setUpdatingTransferId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<ExternalTransferStatus>('pending');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTransfers = useCallback(async (status: ExternalTransferStatus = selectedStatus) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getExternalTransfers({ status });
      setTransfers(response);
    } catch (err) {
      console.error('Error fetching external transfers:', err);
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las transferencias.');
    } finally {
      setLoading(false);
    }
  }, [getExternalTransfers, selectedStatus]);

  const handleApproveTransfer = useCallback(
    async (transferId: string) => {
      const transfer = transfers.find((item) => item.id === transferId);
      if (!transfer || getTransferUiStatus(transfer) !== 'pending') {
        return;
      }

      setStatusUpdateError(null);
      setUpdatingTransferId(transferId);

      try {
        await updateExternalTransferStatus(transferId, 'completed');
        await fetchTransfers(selectedStatus);
      } catch (err) {
        console.error('Error updating transfer status:', err);
        setStatusUpdateError(
          err instanceof Error ? err.message : 'No se pudo actualizar el estado de la transferencia.'
        );
      } finally {
        setUpdatingTransferId(null);
      }
    },
    [fetchTransfers, selectedStatus, transfers, updateExternalTransferStatus]
  );

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      return;
    }
    fetchTransfers(selectedStatus);
  }, [fetchTransfers, isAuthenticated, accessToken, selectedStatus]);

  const pendingCount = useMemo(
    () => transfers.filter((t) => getTransferUiStatus(t) === 'pending').length,
    [transfers]
  );

  const filteredTransfers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return transfers.filter((transfer) => {
      if (!term) {
        return true;
      }

      const metadata = transfer.metadata as Record<string, unknown> | undefined;
      const textMatches = [
        transfer.reference,
        transfer.beneficiary_name,
        transfer.bank_name,
        transfer.clabe,
        transfer.description,
        transfer.emitter_name,
        transfer.emitter_phone,
        transfer.emitter_email,
        metadata ? metadata['concept'] : undefined,
        metadata ? metadata['note'] : undefined,
      ]
        .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
        .some((value) => String(value).toLowerCase().includes(term));

      return textMatches;
    });
  }, [transfers, searchTerm]);

  const totalAmount = useMemo(() => {
    return filteredTransfers.reduce((acc, transfer) => acc + Number(transfer.amount ?? 0), 0);
  }, [filteredTransfers]);

  const defaultCurrency = 'MXN';
  const showActionColumn = selectedStatus === 'pending';
  const tableColSpan = showActionColumn ? 8 : 7;

  return (
    <div className="space-y-6">
      <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-secondary-900 flex items-center gap-2">
            <Banknote className="h-6 w-6 text-primary-500" />
            Transferencias Externas
          </h2>
          <p className="text-secondary-600 text-sm lg:text-base">
            Revisa transferencias externas y aprueba manualmente las pendientes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchTransfers(selectedStatus)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-secondary-200 text-secondary-600 hover:bg-secondary-50 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="card p-4">
          <p className="text-sm text-secondary-500">Transferencias filtradas</p>
          <p className="text-2xl font-semibold text-secondary-900">{filteredTransfers.length}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-secondary-500">Monto total</p>
          <p className="text-2xl font-semibold text-secondary-900">
            {formatCurrency(totalAmount, defaultCurrency)}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-secondary-500">Pendientes</p>
          <p className="text-2xl font-semibold text-amber-700">{pendingCount}</p>
        </div>
      </section>

      <div className="card p-4 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-secondary-200 bg-white p-1">
              <button
                onClick={() => setSelectedStatus('pending')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  selectedStatus === 'pending'
                    ? 'bg-amber-100 text-amber-800'
                    : 'text-secondary-600 hover:bg-secondary-50'
                }`}
              >
                Pendientes
              </button>
              <button
                onClick={() => setSelectedStatus('completed')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  selectedStatus === 'completed'
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'text-secondary-600 hover:bg-secondary-50'
                }`}
              >
                Aprobadas
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por referencia, emisor, beneficiario, banco o CLABE..."
                className="input-field pl-10 w-full lg:w-72"
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Error al cargar las transferencias</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {statusUpdateError && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Error al actualizar la transferencia</p>
              <p className="text-sm">{statusUpdateError}</p>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Referencia
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Monto / Cantidad
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Solicitante
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Beneficiario
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Banco / CLABE
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                  Fecha
                </th>
                {showActionColumn && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 uppercase tracking-wider">
                    Acción
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan} className="px-4 py-8">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
                    </div>
                  </td>
                </tr>
              ) : filteredTransfers.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="px-4 py-10 text-center text-secondary-500">
                    No se encontraron transferencias externas para los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                filteredTransfers.map((transfer) => {
                  const transferStatus = getTransferUiStatus(transfer);
                  const statusVariant = getStatusVariant(transfer.rawStatus ?? transfer.status);
                  const statusClasses = statusVariantColors[statusVariant];
                  const isSaving = updatingTransferId === transfer.id;
                  const requesterName =
                    transfer.user
                      ? [transfer.user.first_name, transfer.user.last_name].filter(Boolean).join(' ').trim()
                      : transfer.emitter_name;
                  const requesterEmail = transfer.user?.email ?? transfer.emitter_email;
                  const requesterPhone = transfer.user?.phone_number ?? transfer.emitter_phone;

                  return (
                    <tr key={transfer.id} className="hover:bg-secondary-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-secondary-900">{transfer.reference || '—'}</div>
                        <div className="text-xs text-secondary-500">ID: {transfer.id}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold text-lg text-secondary-900">
                          {formatCurrency(Number(transfer.amount ?? 0), defaultCurrency)}
                        </div>
                        <div className="text-xs text-secondary-500">
                          {transfer.fee_amount > 0 && `Comisión: ${formatCurrency(transfer.fee_amount, defaultCurrency)}`}
                          {transfer.tax_amount > 0 && ` | Impuesto: ${formatCurrency(transfer.tax_amount, defaultCurrency)}`}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold text-sm text-secondary-900">
                          {requesterName || '—'}
                        </div>
                        {requesterPhone && (
                          <div className="text-xs text-secondary-500">Tel: {requesterPhone}</div>
                        )}
                        {requesterEmail && (
                          <div className="text-xs text-secondary-500">{requesterEmail}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold text-sm text-secondary-900">
                          {transfer.beneficiary_name || '—'}
                        </div>
                        {transfer.description && (
                          <div className="text-xs text-secondary-500">{transfer.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold text-sm text-secondary-900">
                          {transfer.bank_name || '—'}
                        </div>
                        <div className="text-xs text-secondary-500">CLABE: {transfer.clabe || '—'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusClasses}`}
                        >
                          {statusLabels[transferStatus]}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-secondary-600 text-sm">
                        {formatDateTime(transfer.created_at)}
                      </td>
                      {showActionColumn && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => handleApproveTransfer(transfer.id)}
                            disabled={isSaving}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {isSaving ? 'Aprobando...' : 'Aprobar'}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExternalTransfersSection;
